import { useState, useMemo, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import usePartners from "./usePartners";
import {
  useSyncToPartners,
  isProductSyncSupported,
  validatePartnerSyncCount,
  formatPartnerNames,
  partnerSyncToasts,
} from "./useSyncToPartners";
import { partnerIcons } from "src/utils/partnerIcons";
import { formatNumber } from "src/utils/numbers";
import {
  EnumActionType,
  MenuItem,
} from "src/components/neighborhoodSearch/savedSearch/ActionsMenu";

interface UsePartnerSyncOptions {
  productType: string | (() => string);
  actionCount: number;
  getRecordIds: () => Promise<(number | string)[]> | (number | string)[];
}

interface PartnerSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmText: string;
  onConfirm: () => void;
  isLoading: boolean;
}

export function useSyncToPartnersMenu({
  productType: productTypeOrGetter,
  actionCount,
  getRecordIds,
}: UsePartnerSyncOptions) {
  const getProductType = useMemo(
    () => typeof productTypeOrGetter === "function"
      ? productTypeOrGetter
      : () => productTypeOrGetter,
    [productTypeOrGetter]
  );

  const { data: partners = [] } = usePartners();
  const { mutate: syncToPartners, isPending: isSyncingToPartners } = useSyncToPartners();

  // Filter to only Rollout partners that support manual sync
  const enabledPartners = useMemo(
    () => partners.filter((p: { enabled: boolean; isRollout?: boolean }) => p.enabled && p.isRollout),
    [partners]
  );

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    partnerId?: string;
    partnerName: string;
    isSinglePartner: boolean;
  }>({ open: false, partnerName: "", isSinglePartner: true });

  const isManualSyncEnabled = process.env.REACT_APP_ENABLE_MANUAL_PARTNER_SYNC === "true";
  const showPartnerSync = isManualSyncEnabled && enabledPartners.length > 0 && isProductSyncSupported(getProductType());

  // Execute the actual sync
  const executeSync = useCallback(async (partnerId?: string) => {
    const { partnerName } = formatPartnerNames(enabledPartners, partnerId);
    const recordIds = await getRecordIds();

    syncToPartners(
      { product: getProductType(), recordIds, partner: partnerId },
      {
        onSuccess: () => partnerSyncToasts.started(partnerName, recordIds.length),
        onError: () => partnerSyncToasts.failed(partnerName),
      }
    );
  }, [enabledPartners, getRecordIds, getProductType, syncToPartners]);

  // Handle sync request (validates and either syncs directly or shows confirm)
  const handleSyncRequest = useCallback((partnerId?: string) => {
    const validation = validatePartnerSyncCount(actionCount);
    if (validation === "blocked") return;

    if (validation === "proceed") {
      executeSync(partnerId);
      return;
    }

    const { partnerName, isSinglePartner } = formatPartnerNames(enabledPartners, partnerId);
    setConfirmState({ open: true, partnerId, partnerName, isSinglePartner });
  }, [actionCount, enabledPartners, executeSync]);

  // Generate menu items
  const partnerSyncMenuItems: MenuItem[] = useMemo(() => {
    if (!showPartnerSync) return [];

    const items: MenuItem[] = [];

    // Individual partner items
    enabledPartners.forEach((partner: { id: string; name: string }) => {
      const icon = partnerIcons[partner.id];
      items.push({
        type: EnumActionType.SyncToPartner,
        icon: icon ? (
          <img src={icon} alt={partner.name} className="h-4 w-4 object-contain" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        ),
        label: `Sync to ${partner.name} (${formatNumber(actionCount, "0")})`,
        disabled: actionCount === 0 || isSyncingToPartners,
        onAction: () => handleSyncRequest(partner.id),
      });
    });

    // "Sync to All" if multiple partners
    if (enabledPartners.length > 1) {
      items.push({
        type: EnumActionType.SyncToAllPartners,
        icon: <RefreshCw className="h-4 w-4" />,
        label: `Sync to All Partners (${formatNumber(actionCount, "0")})`,
        disabled: actionCount === 0 || isSyncingToPartners,
        onAction: () => handleSyncRequest(),
      });
    }

    return items;
  }, [showPartnerSync, enabledPartners, actionCount, isSyncingToPartners, handleSyncRequest]);

  // Generate dialog props
  const partnerSyncDialogProps: PartnerSyncDialogProps = useMemo(() => ({
    open: confirmState.open,
    onOpenChange: (open: boolean) => setConfirmState(prev => ({ ...prev, open })),
    title: confirmState.isSinglePartner ? "Sync to Partner" : "Sync to Partners",
    description: (
      <>
        Are you sure you want to sync <strong>{formatNumber(actionCount, "0")}</strong>{" "}
        listing{actionCount === 1 ? "" : "s"} to <strong>{confirmState.partnerName}</strong>?
      </>
    ),
    confirmText: "Sync",
    onConfirm: () => executeSync(confirmState.partnerId),
    isLoading: isSyncingToPartners,
  }), [confirmState, actionCount, executeSync, isSyncingToPartners]);

  return {
    showPartnerSync,
    partnerSyncMenuItems,
    partnerSyncDialogProps,
  };
}
